import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { IAccountLogo } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IGetAccountLogosResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: IAccountLogo[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetAccountLogosResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getAccountLogosAsync = createAsyncThunk(
  'savings/accountLogos/get',
  async (pageSize: number | undefined, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const query = pageSize ? `?PageSize=${pageSize}` : '';

      const { data } = await api.get<IGetAccountLogosResponse>(
        `/savings/account-logos${query}`,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const accountLogosGetSlice = createSlice({
  name: 'savings/accountLogos/get',
  initialState,
  reducers: {
    resetGetAccountLogos(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountLogosAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getAccountLogosAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetAccountLogosResponse;
      })
      .addCase(getAccountLogosAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetGetAccountLogos } = accountLogosGetSlice.actions;

export default accountLogosGetSlice.reducer;
